import * as React from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'ctc-view-mode';

export type ViewMode = 'admin' | 'user';

/**
 * Lets a Global.Admin temporarily preview the chrome a regular user sees
 * (no Administration section, picker-only site flow). Stored in localStorage
 * so the choice persists across reloads. Non-admins are always in 'user' mode
 * and the toggle is a no-op for them.
 */
export function useViewMode(): {
  viewMode: ViewMode;
  setViewMode: (m: ViewMode) => void;
  toggleViewMode: () => void;
  canSwitchView: boolean;
} {
  const { isGlobalAdmin } = useAuth();

  const [stored, setStored] = React.useState<ViewMode>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === 'admin' || raw === 'user') return raw;
    } catch {
      /* ignore */
    }
    return 'admin';
  });

  const viewMode: ViewMode = isGlobalAdmin ? stored : 'user';

  const setViewMode = React.useCallback((m: ViewMode) => {
    setStored(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleViewMode = React.useCallback(() => {
    setViewMode(viewMode === 'admin' ? 'user' : 'admin');
  }, [viewMode, setViewMode]);

  return { viewMode, setViewMode, toggleViewMode, canSwitchView: isGlobalAdmin };
}
